import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button, Input, Card, CardBody, Alert } from '@/components/ui';

export default function LoginPage() {
  const [mode, setMode] = useState<'password' | 'apikey'>('password');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { login, setApiKey: setApiKeyAuth } = useAuth();
  const navigate = useNavigate();

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await login(email, password);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApiKeyLogin = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    
    if (!apiKey.trim()) {
      setError('API key is required');
      return;
    }

    setApiKeyAuth(apiKey);
    navigate('/dashboard');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 p-4">
      <Card className="w-full max-w-md">
        <CardBody className="p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <span className="text-5xl">🏠</span>
            <h1 className="text-2xl font-bold text-gray-900 mt-2">PRISAI</h1>
            <p className="text-gray-600">Property Records Intelligence System</p>
          </div>

          {/* Mode toggle */}
          <div className="flex rounded-lg bg-gray-100 p-1 mb-6">
            <button
              onClick={() => setMode('password')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'password' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
              }`}
            >
              Password Login
            </button>
            <button
              onClick={() => setMode('apikey')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                mode === 'apikey' ? 'bg-white shadow text-gray-900' : 'text-gray-600'
              }`}
            >
              API Key
            </button>
          </div>

          {/* Error alert */}
          {error && (
            <Alert variant="error" className="mb-6">
              {error}
            </Alert>
          )}

          {/* Password login form */}
          {mode === 'password' ? (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <Input
                label="Email"
                type="email" autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
              />
              <Input
                label="Password"
                type="password" autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />
              <Button type="submit" isLoading={isLoading} className="w-full">
                Sign In
              </Button>
            </form>
          ) : (
            <form onSubmit={handleApiKeyLogin} className="space-y-4">
              <Input
                label="API Key"
                type="text"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder="pra_..."
                required
              />
              <Button type="submit" className="w-full">
                Authenticate
              </Button>
            </form>
          )}

          {/* Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Access requires authentication.</p>
            <p className="mt-1">Contact your administrator if you need credentials.</p>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
